import { Helmet } from 'react-helmet-async';

const SITE = 'https://www.namestajsanin.com';
/** Deljenje linkova: ista statička slika kao public/logo.png (~95 KB, 1250×1250). */
const SHARE_IMAGE = `${SITE}/logo.png`;

const HelmetConfig = {
  home: {
    title: 'Nameštaj Sanin Prijepolje | Nameštaj po meri Prijepolje i Srbija',
    description:
      'Nameštaj Sanin iz Prijepolja: izrada nameštaja po meri za dom i posao. Garniture, lože, kreveti i stolice. Kvalitetna radionica u Prijepolju — kupci širom Srbije.',
    keywords:
      'namestaj prijepolje, nameštaj prijepolje, namestaj sanin, nameštaj sanin, namestaj po meri prijepolje, nameštaj po meri prijepolje, namestaj po meri srbija, nameštaj po meri Srbija, izrada namestaja po meri, namestaj po meri, garniture prijepolje, kreveti po meri, stolice po meri',
    canonical: `${SITE}/`,
    structuredData: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'FurnitureStore',
          '@id': `${SITE}/#store`,
          name: 'Nameštaj Sanin',
          alternateName: ['Namestaj Sanin', 'Sanin nameštaj Prijepolje'],
          url: `${SITE}/`,
          image: `${SITE}/logo.png`,
          logo: `${SITE}/favicon.png`,
          telephone: '+381641722224',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Prijepolje',
            addressRegion: 'Zlatiborski okrug',
            addressCountry: 'RS',
          },
          areaServed: [
            { '@type': 'City', name: 'Prijepolje' },
            { '@type': 'AdministrativeArea', name: 'Zlatiborski okrug' },
            { '@type': 'Country', name: 'Srbija' },
          ],
          description:
            'Radionica nameštaja po meri u Prijepolju: garniture, lože, kreveti i stolice. Porudžbine i isporuka širom Srbije.',
        },
        {
          '@type': 'WebSite',
          '@id': `${SITE}/#website`,
          url: `${SITE}/`,
          name: 'Nameštaj Sanin Prijepolje',
          description:
            'Nameštaj po meri — Prijepolje i Srbija. Nameštaj Sanin: garniture, lože, kreveti, stolice.',
          inLanguage: 'sr-RS',
          publisher: { '@id': `${SITE}/#store` },
        },
      ],
    },
  },
  garniture: {
    title: 'Garniture po meri Prijepolje | Nameštaj Sanin',
    description:
      'Garniture po meri iz Prijepolja — Nameštaj Sanin. Udobne garniture prilagođene prostoru; izrada za dom širom Srbije.',
    keywords:
      'garniture prijepolje, garniture po meri, garniture po meri srbija, namestaj sanin garniture, namestaj po meri prijepolje',
    canonical: `${SITE}/proizvodi/garniture`,
  },
  loze: {
    title: 'Lože i spavaće sobe po meri | Nameštaj Sanin Prijepolje',
    description:
      'Lože i spavaće sobe po meri — Nameštaj Sanin, Prijepolje. Kvalitetna izrada nameštaja po meri za kupce u Srbiji.',
    keywords:
      'loze prijepolje, lože po meri, spavace sobe po meri, namestaj po meri prijepolje, namestaj po meri srbija',
    canonical: `${SITE}/proizvodi/loze`,
  },
  kreveti: {
    title: 'Kreveti po meri Prijepolje | Nameštaj Sanin',
    description:
      'Kreveti po meri iz radionice Nameštaj Sanin, Prijepolje. Stabilni, udobni kreveti — porudžbine iz cele Srbije.',
    keywords:
      'kreveti prijepolje, krevet po meri, kreveti po meri srbija, namestaj sanin kreveti',
    canonical: `${SITE}/proizvodi/kreveti`,
  },
  stolice: {
    title: 'Stolice po meri Prijepolje | Nameštaj Sanin',
    description:
      'Stolice po meri za trpezariju, kancelariju i ugostiteljstvo — Nameštaj Sanin, Prijepolje. Isporuka širom Srbije.',
    keywords:
      'stolice prijepolje, stolice po meri, namestaj prijepolje stolice, namestaj po meri srbija',
    canonical: `${SITE}/proizvodi/stolice`,
  },
  sve: {
    title: 'Svi proizvodi — nameštaj po meri | Nameštaj Sanin Prijepolje',
    description:
      'Katalog: garniture, lože, kreveti i stolice po meri. Nameštaj Sanin — radionica u Prijepolju, ponuda za celu Srbiju.',
    keywords:
      'svi proizvodi namestaj, namestaj sanin prijepolje, namestaj po meri, namestaj po meri srbija, namestaj prijepolje',
    canonical: `${SITE}/proizvodi/sve`,
  },
};

function breadcrumbJsonLd(pageKey, canonical) {
  const labels = {
    garniture: 'Garniture',
    loze: 'Lože',
    kreveti: 'Kreveti',
    stolice: 'Stolice',
    sve: 'Svi proizvodi',
  };
  const name = labels[pageKey];
  if (!name) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Početna',
        item: `${SITE}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name,
        item: canonical,
      },
    ],
  };
}

export function PageHelmet({ page }) {
  const config = HelmetConfig[page] || HelmetConfig.home;
  const crumbs = page !== 'home' ? breadcrumbJsonLd(page, config.canonical) : null;

  return (
    <Helmet>
      <html lang="sr" />
      <title>{config.title}</title>
      <meta name="description" content={config.description} />
      <meta name="keywords" content={config.keywords} />
      <link rel="canonical" href={config.canonical} />

      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="language" content="sr" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta property="og:locale" content="sr_RS" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Nameštaj Sanin" />
      <meta property="og:title" content={config.title} />
      <meta property="og:description" content={config.description} />
      <meta property="og:url" content={config.canonical} />
      <meta property="og:image" content={SHARE_IMAGE} />
      <meta property="og:image:secure_url" content={SHARE_IMAGE} />
      <meta property="og:image:width" content="1250" />
      <meta property="og:image:height" content="1250" />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:alt" content="Nameštaj Sanin — logo" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={config.title} />
      <meta name="twitter:description" content={config.description} />
      <meta name="twitter:image" content={SHARE_IMAGE} />

      {config.structuredData && (
        <script type="application/ld+json">{JSON.stringify(config.structuredData)}</script>
      )}
      {crumbs && (
        <script type="application/ld+json">{JSON.stringify(crumbs)}</script>
      )}
    </Helmet>
  );
}

export default HelmetConfig;
